import {
    type MetricsFields,
    type PageMetrics,
    type MetricsConfiguration,
    PageInvocationPoint,
} from '@jet/environment/types/metrics';
import { type Opt, isNothing } from '@jet/environment';
import { DEFAULT_EVENT_TOPIC } from '@jet-app/music-classical/utilities/constants';

export enum PageType {
    artistPage = 'artistPage',
    browsePage = 'browsePage',
    detailPage = 'detailPage',
    homePage = 'homePage',
    playlistPage = 'playlistPage',
    seeAllPage = 'seeAllPage',
    searchPage = 'searchPage',
    libraryAllPlaylistsPage = 'allPlaylistsPage',
    libraryListPage = 'libraryListPage',
    libraryTracksPage = 'libraryTracksPage',
}

function pageFieldFromTypeAndID(
    pageType: PageType,
    pageId?: Opt<string>,
): Opt<string> {
    if (isNothing(pageId)) {
        return null;
    }

    return `${pageType}_${pageId}`;
}

export function createPageMetricsForResponse(
    response: any,
    pageType: PageType,
    pageId: Opt<string> = null,
    customPageFields?: Opt<MetricsFields>,
): PageMetrics {
    const pageFields: MetricsFields = customPageFields ?? {};

    const resourceIdentifier = response.body?.data?.[0];
    const pageIdOrFallback = pageId ?? resourceIdentifier?.id;

    pageFields['pageType'] = pageType;

    if (pageIdOrFallback) {
        pageFields['pageId'] = pageIdOrFallback;
    }

    const page = pageFieldFromTypeAndID(pageType, pageIdOrFallback);
    if (page) {
        pageFields['page'] = page;
    }

    return {
        instructions: [
            {
                data: {
                    topic: DEFAULT_EVENT_TOPIC,
                    shouldFlush: false,
                    fields: { eventType: 'page' },
                    includingFields: ['pageFields', 'languages'],
                    excludingFields: [],
                },
                invocationPoints: [PageInvocationPoint.pageEnter],
            },
        ],
        pageFields,
        custom: {},
    };
}

// tslint:disable-next-line:variable-name
export const DefaultMetricsConfiguration: MetricsConfiguration = {
    defaultTopic: DEFAULT_EVENT_TOPIC,
    defaultIncludeRequests: {
        click: ['pageFields', 'languages'],
        page: ['pageFields', 'languages'],
        search: ['pageFields', 'languages'],
        impressions: ['pageFields', 'languages'],
        media: ['pageFields', 'languages'],
        pageRender: ['pageFields', 'languages'],
    },
    defaultExcludeRequests: {
        click: [],
        page: [],
        search: [],
        impressions: [],
        media: [],
        pageRender: [],
    },
    shouldFlush: null,
};

export class Metrics {
    configuration: MetricsConfiguration;
    constructor() {
        this.configuration = DefaultMetricsConfiguration;
    }
}
